import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CachedVariant } from '@app/services/featureFlag/types';

/**
 * Single source of truth for A/B-testing variants.
 *
 * The whole map is owned by one `userId`; whenever the active user changes the
 * map is dropped, so a variant resolved for user A can never leak to user B
 * (important for the web/iframe build where the partner platform swaps users).
 *
 * This slice is persisted (whitelisted in the store config), so it survives
 * app restarts on both mobile (AsyncStorage) and web (localStorage).
 */
export interface FeatureFlagState {
  /** The user the cached variants belong to. */
  userId: string | null;
  /** Map of featureFlagName -> cached variant. */
  variants: Record<string, CachedVariant>;
}

const initialState: FeatureFlagState = {
  userId: null,
  variants: {},
};

export const featureFlagSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    /**
     * Store a resolved variant. If it belongs to a different (or not-yet-set)
     * user, the existing map is wiped first so the cache stays single-user.
     */
    setFeatureFlagVariant: (
      state,
      action: PayloadAction<{
        userId: string;
        featureFlagName: string;
        variant: CachedVariant;
      }>,
    ) => {
      const { userId, featureFlagName, variant } = action.payload;
      if (state.userId !== userId) {
        state.userId = userId;
        state.variants = {};
      }
      state.variants[featureFlagName] = variant;
    },

    /**
     * Silently drop the cache if it belongs to a different user. Call this when
     * the active user is (re)resolved — e.g. after the users API responds.
     */
    reconcileFeatureFlagUser: (
      state,
      action: PayloadAction<{ userId: string }>,
    ) => {
      const { userId } = action.payload;
      if (state.userId && state.userId !== userId) {
        state.userId = userId;
        state.variants = {};
      }
    },

    /** Wipe everything (used on logout). */
    clearFeatureFlags: () => {
      console.log('[featureFlags] clearFeatureFlags called — wiping all cached variants');
      return initialState;
    },
  },
});

export const {
  setFeatureFlagVariant,
  reconcileFeatureFlagUser,
  clearFeatureFlags,
} = featureFlagSlice.actions;

export default featureFlagSlice.reducer;
